import Oystehr, { User } from '@oystehr/sdk';
import { Schedule, Slot } from 'fhir/r4b';
import {
  AiAssistedEncountersReportZambdaInput,
  AiAssistedEncountersReportZambdaOutput,
  apiErrorToThrow,
  ApplyTemplateZambdaInput,
  ApplyTemplateZambdaOutput,
  AssignPractitionerInput,
  AssignPractitionerResponse,
  BulkUpdateInsuranceStatusInput,
  BulkUpdateInsuranceStatusResponse,
  CancelAppointmentZambdaInput,
  CancelAppointmentZambdaOutput,
  CancelRadiologyOrderZambdaInput,
  CancelRadiologyOrderZambdaOutput,
  CancelTelemedAppointmentZambdaInput,
  CancelTelemedAppointmentZambdaOutput,
  ChangeInPersonVisitStatusInput,
  ChangeInPersonVisitStatusResponse,
  chooseJson,
  CollectInHouseLabSpecimenParameters,
  CollectInHouseLabSpecimenZambdaOutput,
  CreateAppointmentInputParams,
  CreateAppointmentResponse,
  CreateDischargeSummaryInput,
  CreateDischargeSummaryResponse,
  CreateInHouseLabOrderParameters,
  CreateInHouseLabOrderResponse,
  CreateLabOrderParameters,
  CreateLabOrderZambdaOutput,
  CreateNursingOrderInput,
  CreateRadiologyZambdaOrderInput,
  CreateRadiologyZambdaOrderOutput,
  CreateResourcesFromAudioRecordingInput,
  CreateResourcesFromAudioRecordingOutput,
  CreateScheduleParams,
  CreateSlotParams,
  CreateUploadAudioRecordingInput,
  CreateUploadAudioRecordingOutput,
  CreateUserOutput,
  CreateUserParams,
  DailyPaymentsReportZambdaInput,
  DailyPaymentsReportZambdaOutput,
  DeleteInHouseLabOrderParameters,
  DeleteInHouseLabOrderZambdaOutput,
  DeleteLabOrderZambdaInput,
  DeleteLabOrderZambdaOutput,
  DownloadPatientProfilePhotoInput,
  EHRVisitDetails,
  GetAppointmentsZambdaInput,
  GetAppointmentsZambdaOutput,
  GetConversationInput,
  GetConversationZambdaOutput,
  GetCreateInHouseLabOrderResourcesParameters,
  GetCreateInHouseLabOrderResourcesResponse,
  GetEmployeesResponse,
  GetInHouseOrdersParameters,
  GetLabelPdfParameters,
  GetLabOrdersParameters,
  GetNursingOrdersInput,
  GetOrUploadPatientProfilePhotoZambdaResponse,
  GetPresignedFileURLInput,
  GetRadiologyOrderListZambdaInput,
  GetRadiologyOrderListZambdaOutput,
  GetScheduleParams,
  GetScheduleRequestParams,
  GetScheduleResponse,
  GetUserParams,
  GetUserResponse,
  GetVisitDetailsPDFInput,
  GetVisitLabelInput,
  HandleInHouseLabResultsParameters,
  HandleInHouseLabResultsZambdaOutput,
  Icd10SearchRequestParams,
  Icd10SearchResponse,
  IncompleteEncountersReportZambdaInput,
  IncompleteEncountersReportZambdaOutput,
  InHouseGetOrdersResponseDTO,
  InviteParticipantRequestParameters,
  LabelPdf,
  ListScheduleOwnersParams,
  ListScheduleOwnersResponse,
  ListTemplatesZambdaInput,
  ListTemplatesZambdaOutput,
  PaginatedResponse,
  PaperworkToPDFInput,
  PendingSupervisorApprovalInput,
  PresignUploadUrlResponse,
  QuickText,
  RadiologyLaunchViewerZambdaInput,
  RadiologyLaunchViewerZambdaOutput,
  SaveFollowupEncounterZambdaInput,
  SaveFollowupEncounterZambdaOutput,
  ScheduleDTO,
  SendReceiptByEmailZambdaInput,
  SendReceiptByEmailZambdaOutput,
  SubmitLabOrderInput,
  SubmitLabOrderOutput,
  UnassignPractitionerZambdaInput,
  UnassignPractitionerZambdaOutput,
  UnreadMessagesInput,
  UnreadMessagesOutput,
  UpdateInvoiceTaskZambdaInput,
  UpdateLabOrderResourcesInput,
  UpdateNursingOrderInput,
  UpdateScheduleParams,
  UpdateUserParams,
  UpdateUserZambdaOutput,
  UpdateVisitDetailsInput,
  UpdateVisitFilesInput,
  UploadPatientProfilePhotoInput,
  UserActivationZambdaInput,
  UserActivationZambdaOutput,
  VisitDocuments,
  VisitsOverviewReportZambdaInput,
  VisitsOverviewReportZambdaOutput,
} from 'utils';

export interface PatchOperation {
  // https://www.hl7.org/fhir/fhirpatch.html
  op: 'add' | 'insert' | 'delete' | 'replace' | 'move';
  path: string;
  value: string | object | boolean;
}

const VITE_APP_IS_LOCAL = import.meta.env.VITE_APP_IS_LOCAL;
const SUBMIT_LAB_ORDER_ZAMBDA_ID = 'submit-lab-order';
const GET_APPOINTMENTS_ZAMBDA_ID = 'get-appointments';
const INCOMPLETE_ENCOUNTERS_REPORT_ZAMBDA_ID = 'incomplete-encounters-report';
const AI_ASSISTED_ENCOUNTERS_REPORT_ZAMBDA_ID = 'ai-assisted-encounters-report';
const DAILY_PAYMENTS_REPORT_ZAMBDA_ID = 'daily-payments-report';
const VISITS_OVERVIEW_REPORT_ZAMBDA_ID = 'visits-overview-report';
const CREATE_APPOINTMENT_ZAMBDA_ID = 'create-appointment';
const CANCEL_TELEMED_APPOINTMENT_ZAMBDA_ID = 'telemed-cancel-appointment';
const INVITE_PARTICIPANT_ZAMBDA_ID = 'video-chat-invites-create';
const CREATE_USER_ZAMBDA_ID = 'create-user';
const UPDATE_USER_ZAMBDA_ID = 'update-user';
const ASSIGN_PRACTITIONER_ZAMBDA_ID = 'assign-practitioner';
const UNASSIGN_PRACTITIONER_ZAMBDA_ID = 'unassign-practitioner';
const CHANGE_IN_PERSON_VISIT_STATUS_ZAMBDA_ID = 'change-in-person-visit-status';
const GET_USER_ZAMBDA_ID = 'get-user';
const USER_ACTIVATION_ZAMBDA_ID = 'user-activation';
const GET_CONVERSATION_ZAMBDA_ID = 'get-conversation';
const GET_SCHEDULE_ZAMBDA_ID = 'get-schedule';
const CANCEL_APPOINTMENT_ZAMBDA_ID = 'cancel-appointment';
const GET_EMPLOYEES_ZAMBDA_ID = 'get-employees';
const GET_PATIENT_PROFILE_PHOTO_URL_ZAMBDA_ID = 'get-patient-profile-photo-url';
const SAVE_PATIENT_FOLLOWUP_ZAMBDA_ID = 'save-followup-encounter';
const CREATE_LAB_ORDER_ZAMBDA_ID = 'create-lab-order';
const GET_LAB_ORDERS_ZAMBDA_ID = 'get-lab-orders';
const DELETE_LAB_ORDER_ZAMBDA_ID = 'delete-lab-order';
const UPDATE_LAB_ORDER_RESOURCES_ZAMBDA_ID = 'update-lab-order-resources';
const EHR_GET_SCHEDULE_ZAMBDA_ID = 'ehr-get-schedule';
const UPDATE_SCHEDULE_ZAMBDA_ID = 'update-schedule';
const LIST_SCHEDULE_OWNERS_ZAMBDA_ID = 'list-schedule-owners';
const CREATE_SCHEDULE_ZAMBDA_ID = 'create-schedule';
const CREATE_SLOT_ZAMBDA_ID = 'create-slot';
const CREATE_IN_HOUSE_LAB_ORDER_ZAMBDA_ID = 'create-in-house-lab-order';
const GET_IN_HOUSE_ORDERS_ZAMBDA_ID = 'get-in-house-orders';
const GET_CREATE_IN_HOUSE_LAB_ORDER_RESOURCES = 'get-create-in-house-lab-order-resources';
const COLLECT_IN_HOUSE_LAB_SPECIMEN = 'collect-in-house-lab-specimen';
const HANDLE_IN_HOUSE_LAB_RESULTS = 'handle-in-house-lab-results';
const DELETE_IN_HOUSE_LAB_ORDER = 'delete-in-house-lab-order';
const UNLOCK_APPOINTMENT_ZAMBDA_ID = 'unlock-appointment';
const GET_NURSING_ORDERS_ZAMBDA_ID = 'get-nursing-orders';
const CREATE_NURSING_ORDER_ZAMBDA_ID = 'create-nursing-order';
const UPDATE_NURSING_ORDER = 'update-nursing-order';
const GET_LABEL_PDF_ZAMBDA_ID = 'get-label-pdf';
const UPLOAD_AUDIO_RECORDING_ZAMBDA_ID = 'upload-audio-recording';
const CREATE_RESOURCES_FROM_AUDIO_RECORDING_ZAMBDA_ID = 'create-resources-from-audio-recording';
const GET_OR_CREATE_VISIT_LABEL_PDF_ZAMBDA_ID = 'get-or-create-visit-label-pdf';
const CREATE_DISCHARGE_SUMMARY = 'create-discharge-summary';
const PAPERWORK_TO_PDF_ZAMBDA_ID = 'paperwork-to-pdf';
const VISIT_DETAILS_TO_PDF_ZAMBDA_ID = 'visit-details-to-pdf';
const PENDING_SUPERVISOR_APPROVAL_ZAMBDA_ID = 'pending-supervisor-approval';
const SEND_RECEIPT_BY_EMAIL_ZAMBDA_ID = 'send-receipt-by-email';
const INVOICEABLE_PATIENTS_REPORT_ZAMBDA_ID = 'invoiceable-patients-report';
const BULK_UPDATE_INSURANCE_STATUS_ZAMBDA_ID = 'bulk-update-insurance-status';
const UPDATE_INVOICE_TASK_ZAMBDA_ID = 'update-invoice-task';
const GET_PATIENT_QUEUE_ZAMBDA_ID = 'patient-queue';
const GET_QUICK_TEXT_ZAMBDA_ID = 'get-quick-text';
const DELETE_QUICK_TEXT_ZAMBDA_ID = 'delete-quick-text';
const CREATE_QUICK_TEXT_ZAMBDA_ID = 'create-quick-text';
const PATCH_QUICK_TEXT_ZAMBDA_ID = 'patch-quick-text';

export const getUser = async (token: string): Promise<User> => {
  const oystehr = new Oystehr({
    accessToken: token,
    projectApiUrl: import.meta.env.VITE_APP_PROJECT_API_URL,
  });
  return oystehr.user.me();
};

if (!VITE_APP_IS_LOCAL) {
  throw new Error('VITE_APP_IS_LOCAL is not defined');
}

export const submitLabOrder = async (
  oystehr: Oystehr,
  parameters: SubmitLabOrderInput
): Promise<SubmitLabOrderOutput> => {
  try {
    if (SUBMIT_LAB_ORDER_ZAMBDA_ID == null) {
      throw new Error('submit external lab order zambda environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: SUBMIT_LAB_ORDER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getLabelPdf = async (oystehr: Oystehr, parameters: GetLabelPdfParameters): Promise<LabelPdf[]> => {
  try {
    if (GET_LABEL_PDF_ZAMBDA_ID == null) {
      throw new Error('get-label-pdf environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: GET_LABEL_PDF_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.error(error);
    throw error;
  }
};

export const uploadAudioRecording = async (
  oystehr: Oystehr,
  parameters: CreateUploadAudioRecordingInput
): Promise<CreateUploadAudioRecordingOutput> => {
  try {
    if (UPLOAD_AUDIO_RECORDING_ZAMBDA_ID == null) {
      throw new Error('upload audio recording zambda environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: UPLOAD_AUDIO_RECORDING_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const createResourcesFromAudioRecording = async (
  oystehr: Oystehr,
  parameters: CreateResourcesFromAudioRecordingInput
): Promise<CreateResourcesFromAudioRecordingOutput> => {
  try {
    if (CREATE_RESOURCES_FROM_AUDIO_RECORDING_ZAMBDA_ID == null) {
      throw new Error('create resources from audio recording zambda environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: CREATE_RESOURCES_FROM_AUDIO_RECORDING_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getOrCreateVisitLabel = async (oystehr: Oystehr, parameters: GetVisitLabelInput): Promise<LabelPdf[]> => {
  try {
    if (GET_OR_CREATE_VISIT_LABEL_PDF_ZAMBDA_ID == null) {
      throw new Error('get-or-create-visit-label-pdf environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: GET_OR_CREATE_VISIT_LABEL_PDF_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.error(error);
    throw error;
  }
};

export const getAppointments = async (
  oystehr: Oystehr,
  parameters: GetAppointmentsZambdaInput
): Promise<GetAppointmentsZambdaOutput> => {
  try {
    if (GET_APPOINTMENTS_ZAMBDA_ID == null) {
      throw new Error('get appointments environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: GET_APPOINTMENTS_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getIncompleteEncountersReport = async (
  oystehr: Oystehr,
  parameters: IncompleteEncountersReportZambdaInput
): Promise<IncompleteEncountersReportZambdaOutput> => {
  try {
    if (INCOMPLETE_ENCOUNTERS_REPORT_ZAMBDA_ID == null) {
      throw new Error('incomplete encounters report environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: INCOMPLETE_ENCOUNTERS_REPORT_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getAiAssistedEncountersReport = async (
  oystehr: Oystehr,
  parameters: AiAssistedEncountersReportZambdaInput
): Promise<AiAssistedEncountersReportZambdaOutput> => {
  try {
    if (AI_ASSISTED_ENCOUNTERS_REPORT_ZAMBDA_ID == null) {
      throw new Error('ai-assisted encounters report environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: AI_ASSISTED_ENCOUNTERS_REPORT_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getDailyPaymentsReport = async (
  oystehr: Oystehr,
  parameters: DailyPaymentsReportZambdaInput
): Promise<DailyPaymentsReportZambdaOutput> => {
  try {
    if (DAILY_PAYMENTS_REPORT_ZAMBDA_ID == null) {
      throw new Error('daily payments report environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: DAILY_PAYMENTS_REPORT_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getVisitsOverviewReport = async (
  oystehr: Oystehr,
  parameters: VisitsOverviewReportZambdaInput
): Promise<VisitsOverviewReportZambdaOutput> => {
  try {
    if (VISITS_OVERVIEW_REPORT_ZAMBDA_ID == null) {
      throw new Error('visits overview report environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: VISITS_OVERVIEW_REPORT_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const createAppointment = async (
  oystehr: Oystehr,
  parameters: CreateAppointmentInputParams
): Promise<CreateAppointmentResponse> => {
  try {
    if (CREATE_APPOINTMENT_ZAMBDA_ID == null) {
      throw new Error('create appointment environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: CREATE_APPOINTMENT_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const saveFollowup = async (
  oystehr: Oystehr,
  parameters: SaveFollowupEncounterZambdaInput
): Promise<SaveFollowupEncounterZambdaOutput> => {
  try {
    if (SAVE_PATIENT_FOLLOWUP_ZAMBDA_ID == null) {
      throw new Error('save followup environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: SAVE_PATIENT_FOLLOWUP_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw new Error(JSON.stringify(error));
  }
};

export const cancelTelemedAppointment = async (
  oystehr: Oystehr,
  parameters: CancelTelemedAppointmentZambdaInput
): Promise<CancelTelemedAppointmentZambdaOutput> => {
  try {
    if (CANCEL_TELEMED_APPOINTMENT_ZAMBDA_ID == null) {
      throw new Error('cancel appointment environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: CANCEL_TELEMED_APPOINTMENT_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error, 'error');
    throw new Error(JSON.stringify(error));
  }
};

export const inviteParticipant = async (
  oystehr: Oystehr,
  parameters: InviteParticipantRequestParameters
): Promise<void> => {
  try {
    if (INVITE_PARTICIPANT_ZAMBDA_ID == null) {
      throw new Error('invite participant environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: INVITE_PARTICIPANT_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error, 'Error occurred trying to invite participant');
    throw new Error(JSON.stringify(error));
  }
};

export const createUser = async (oystehr: Oystehr, parameters: CreateUserParams): Promise<CreateUserOutput> => {
  try {
    if (CREATE_USER_ZAMBDA_ID == null) {
      throw new Error('create user environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: CREATE_USER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    throw new Error(JSON.stringify(error));
  }
};

export const updateUser = async (oystehr: Oystehr, parameters: UpdateUserParams): Promise<UpdateUserZambdaOutput> => {
  try {
    if (UPDATE_USER_ZAMBDA_ID == null) {
      throw new Error('update user environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: UPDATE_USER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    throw new Error(JSON.stringify(error));
  }
};

export const assignPractitioner = async (
  oystehr: Oystehr,
  parameters: AssignPractitionerInput
): Promise<AssignPractitionerResponse> => {
  try {
    if (ASSIGN_PRACTITIONER_ZAMBDA_ID == null) {
      throw new Error('assign practitioner environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: ASSIGN_PRACTITIONER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const unassignPractitioner = async (
  oystehr: Oystehr,
  parameters: UnassignPractitionerZambdaInput
): Promise<UnassignPractitionerZambdaOutput> => {
  try {
    if (UNASSIGN_PRACTITIONER_ZAMBDA_ID == null) {
      throw new Error('unassign practitioner environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: UNASSIGN_PRACTITIONER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    throw new Error(JSON.stringify(error));
  }
};

export const changeInPersonVisitStatus = async (
  oystehr: Oystehr,
  parameters: ChangeInPersonVisitStatusInput
): Promise<ChangeInPersonVisitStatusResponse> => {
  try {
    if (CHANGE_IN_PERSON_VISIT_STATUS_ZAMBDA_ID == null) {
      throw new Error('change in person visit status environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: CHANGE_IN_PERSON_VISIT_STATUS_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    throw new Error(JSON.stringify(error));
  }
};

export const getUserDetails = async (oystehr: Oystehr, parameters: GetUserParams): Promise<GetUserResponse> => {
  try {
    if (GET_USER_ZAMBDA_ID == null) {
      throw new Error('get user details environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: GET_USER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    throw new Error(JSON.stringify(error));
  }
};

export const userActivation = async (
  oystehr: Oystehr,
  parameters: UserActivationZambdaInput
): Promise<UserActivationZambdaOutput> => {
  try {
    if (USER_ACTIVATION_ZAMBDA_ID == null) {
      throw new Error('user-activation environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: USER_ACTIVATION_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    throw new Error(JSON.stringify(error));
  }
};

export const getConversation = async (
  oystehr: Oystehr,
  parameters: GetConversationInput
): Promise<GetConversationZambdaOutput> => {
  try {
    if (GET_CONVERSATION_ZAMBDA_ID == null) {
      throw new Error('GET_CONVERSATION_ZAMBDA_ID environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: GET_CONVERSATION_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    throw new Error(JSON.stringify(error));
  }
};

export const getQuickTexts = async (oystehr: Oystehr): Promise<QuickText[]> => {
  try {
    if (GET_QUICK_TEXT_ZAMBDA_ID == null) {
      throw new Error('GET_QUICK_TEXT_ZAMBDA_ID environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: GET_QUICK_TEXT_ZAMBDA_ID,
    });
    const result = chooseJson(response);
    return result.quickTexts || [];
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const deleteQuickText = async (oystehr: Oystehr, quickTextId: string): Promise<void> => {
  try {
    if (DELETE_QUICK_TEXT_ZAMBDA_ID == null) {
      throw new Error('DELETE_QUICK_TEXT_ZAMBDA_ID environment variable could not be loaded');
    }

    await oystehr.zambda.execute({
      id: DELETE_QUICK_TEXT_ZAMBDA_ID,
      quickTextId,
    });
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const createQuickText = async (oystehr: Oystehr, text: string): Promise<QuickText> => {
  try {
    if (CREATE_QUICK_TEXT_ZAMBDA_ID == null) {
      throw new Error('CREATE_QUICK_TEXT_ZAMBDA_ID environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: CREATE_QUICK_TEXT_ZAMBDA_ID,
      text,
    });
    const result = chooseJson(response);
    return result.quickText;
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const patchQuickText = async (oystehr: Oystehr, quickTextId: string, text: string): Promise<QuickText> => {
  try {
    if (PATCH_QUICK_TEXT_ZAMBDA_ID == null) {
      throw new Error('PATCH_QUICK_TEXT_ZAMBDA_ID environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: PATCH_QUICK_TEXT_ZAMBDA_ID,
      quickTextId,
      text,
    });
    const result = chooseJson(response);
    return result.quickText;
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getLocations = async (
  oystehr: Oystehr,
  parameters: GetScheduleRequestParams
): Promise<GetScheduleResponse> => {
  try {
    if (GET_SCHEDULE_ZAMBDA_ID == null || VITE_APP_IS_LOCAL == null) {
      throw new Error('get location environment variable could not be loaded');
    }
    console.log(import.meta.env);
    const response = await oystehr.zambda.executePublic({
      id: GET_SCHEDULE_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const cancelAppointment = async (
  oystehr: Oystehr,
  parameters: CancelAppointmentZambdaInput
): Promise<CancelAppointmentZambdaOutput> => {
  try {
    if (CANCEL_APPOINTMENT_ZAMBDA_ID == null) {
      throw new Error('cancel appointment environment variable could not be loaded');
    }

    const response = await oystehr.zambda.executePublic({
      id: CANCEL_APPOINTMENT_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getEmployees = async (oystehr: Oystehr): Promise<GetEmployeesResponse> => {
  try {
    if (GET_EMPLOYEES_ZAMBDA_ID == null) {
      throw new Error('get employees environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: GET_EMPLOYEES_ZAMBDA_ID,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const listScheduleOwners = async (
  params: ListScheduleOwnersParams,
  oystehr: Oystehr
): Promise<ListScheduleOwnersResponse> => {
  try {
    if (LIST_SCHEDULE_OWNERS_ZAMBDA_ID == null) {
      throw new Error('list-schedule-owners zambda environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: LIST_SCHEDULE_OWNERS_ZAMBDA_ID,
      ...params,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getSchedule = async (params: GetScheduleParams, oystehr: Oystehr): Promise<ScheduleDTO> => {
  try {
    if (EHR_GET_SCHEDULE_ZAMBDA_ID == null) {
      throw new Error('ehr-get-schedule zambda environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: EHR_GET_SCHEDULE_ZAMBDA_ID,
      ...params,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const updateSchedule = async (params: UpdateScheduleParams, oystehr: Oystehr): Promise<Schedule> => {
  try {
    if (UPDATE_SCHEDULE_ZAMBDA_ID == null) {
      throw new Error('update-schedule zambda environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: UPDATE_SCHEDULE_ZAMBDA_ID,
      ...params,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const createSchedule = async (params: CreateScheduleParams, oystehr: Oystehr): Promise<Schedule> => {
  try {
    const response = await oystehr.zambda.execute({
      id: CREATE_SCHEDULE_ZAMBDA_ID,
      ...params,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export type UploadPatientProfilePhotoParameters = Omit<UploadPatientProfilePhotoInput, 'action'> & {
  patientPhotoFile: File;
};

export const uploadPatientProfilePhoto = async (
  oystehr: Oystehr,
  parameters: UploadPatientProfilePhotoParameters
): Promise<GetOrUploadPatientProfilePhotoZambdaResponse> => {
  try {
    if (GET_PATIENT_PROFILE_PHOTO_URL_ZAMBDA_ID == null) {
      throw new Error('Could not find environment variable GET_PATIENT_PROFILE_PHOTO_URL_ZAMBDA_ID');
    }

    const { patientPhotoFile, ...zambdaInput } = parameters;

    const urlSigningResponse = await oystehr.zambda.execute({
      id: GET_PATIENT_PROFILE_PHOTO_URL_ZAMBDA_ID,
      ...zambdaInput,
      action: 'upload',
    });

    const { presignedImageUrl } = chooseJson(urlSigningResponse);

    // Upload the file to S3
    const uploadResponse = await fetch(presignedImageUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': patientPhotoFile.type,
      },
      body: patientPhotoFile,
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    return chooseJson(urlSigningResponse);
  } catch (error: unknown) {
    console.error(error);
    throw error;
  }
};

export type GetPatientProfilePhotoParameters = Omit<DownloadPatientProfilePhotoInput, 'action'>;

export const getSignedPatientProfilePhotoUrl = async (
  oystehr: Oystehr,
  parameters: GetPatientProfilePhotoParameters
): Promise<GetOrUploadPatientProfilePhotoZambdaResponse> => {
  try {
    if (GET_PATIENT_PROFILE_PHOTO_URL_ZAMBDA_ID == null) {
      throw new Error('Could not find environment variable GET_PATIENT_PROFILE_PHOTO_URL_ZAMBDA_ID');
    }

    const urlSigningResponse = await oystehr.zambda.execute({
      id: GET_PATIENT_PROFILE_PHOTO_URL_ZAMBDA_ID,
      ...parameters,
      action: 'download',
    });

    return chooseJson(urlSigningResponse);
  } catch (error: unknown) {
    console.error(error);
    throw error;
  }
};

export const createExternalLabOrder = async (
  oystehr: Oystehr,
  parameters: CreateLabOrderParameters
): Promise<CreateLabOrderZambdaOutput> => {
  try {
    if (CREATE_LAB_ORDER_ZAMBDA_ID == null) {
      throw new Error('create external lab order environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: CREATE_LAB_ORDER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getExternalLabOrders = async <RequestParameters extends GetLabOrdersParameters>(
  oystehr: Oystehr,
  parameters: RequestParameters
): Promise<PaginatedResponse<RequestParameters>> => {
  try {
    if (GET_LAB_ORDERS_ZAMBDA_ID == null) {
      throw new Error('get external lab orders zambda environment variable could not be loaded');
    }
    const { searchBy } = parameters;
    if (!searchBy) {
      throw new Error(
        `Missing one of the required parameters (serviceRequestId | encounterId | patientId): ${JSON.stringify(
          parameters
        )}`
      );
    }
    const response = await oystehr.zambda.execute({
      id: GET_LAB_ORDERS_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const deleteLabOrder = async (
  oystehr: Oystehr,
  parameters: DeleteLabOrderZambdaInput
): Promise<DeleteLabOrderZambdaOutput> => {
  try {
    if (DELETE_LAB_ORDER_ZAMBDA_ID == null) {
      throw new Error('delete lab order zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: DELETE_LAB_ORDER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const updateLabOrderResources = async (
  oystehr: Oystehr,
  parameters: UpdateLabOrderResourcesInput
): Promise<any> => {
  try {
    if (UPDATE_LAB_ORDER_RESOURCES_ZAMBDA_ID == null) {
      throw new Error('update lab order resources zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: UPDATE_LAB_ORDER_RESOURCES_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const createRadiologyOrder = async (
  oystehr: Oystehr,
  parameters: CreateRadiologyZambdaOrderInput
): Promise<CreateRadiologyZambdaOrderOutput> => {
  try {
    const response = await oystehr.zambda.execute({
      id: 'radiology-create-order',
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const cancelRadiologyOrder = async (
  oystehr: Oystehr,
  parameters: CancelRadiologyOrderZambdaInput
): Promise<CancelRadiologyOrderZambdaOutput> => {
  try {
    const response = await oystehr.zambda.execute({
      id: 'radiology-cancel-order',
      ...parameters,
    });
    return response ? chooseJson(response) : {};
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const radiologyLaunchViewer = async (
  oystehr: Oystehr,
  parameters: RadiologyLaunchViewerZambdaInput
): Promise<RadiologyLaunchViewerZambdaOutput> => {
  try {
    const response = await oystehr.zambda.execute({
      id: 'radiology-launch-viewer',
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getRadiologyOrders = async (
  oystehr: Oystehr,
  parameters: GetRadiologyOrderListZambdaInput
): Promise<GetRadiologyOrderListZambdaOutput> => {
  try {
    const searchBy = parameters.encounterIds || parameters.patientId || parameters.serviceRequestId;
    if (!searchBy) {
      throw new Error(
        `Missing one of the required parameters (serviceRequestId | encounterId | patientId): ${JSON.stringify(
          parameters
        )}`
      );
    }
    const response = await oystehr.zambda.execute({
      id: 'radiology-order-list',
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const createSlot = async (input: CreateSlotParams, oystehr: Oystehr): Promise<Slot> => {
  try {
    const response = await oystehr.zambda.executePublic({ id: CREATE_SLOT_ZAMBDA_ID, ...input });
    const jsonToUse = chooseJson(response);
    return jsonToUse;
  } catch (error: unknown) {
    throw apiErrorToThrow(error);
  }
};

export const createInHouseLabOrder = async (
  oystehr: Oystehr,
  parameters: CreateInHouseLabOrderParameters
): Promise<CreateInHouseLabOrderResponse> => {
  try {
    if (CREATE_IN_HOUSE_LAB_ORDER_ZAMBDA_ID == null) {
      throw new Error('create in house lab order zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: CREATE_IN_HOUSE_LAB_ORDER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getInHouseOrders = async <RequestParameters extends GetInHouseOrdersParameters>(
  oystehr: Oystehr,
  parameters: RequestParameters
): Promise<InHouseGetOrdersResponseDTO<RequestParameters>> => {
  try {
    if (GET_IN_HOUSE_ORDERS_ZAMBDA_ID == null) {
      throw new Error('get in house orders zambda environment variable could not be loaded');
    }
    const { searchBy } = parameters;
    if (!searchBy) {
      throw new Error(
        `Missing one of the required parameters (serviceRequestId | encounterId | patientId): ${JSON.stringify(
          parameters
        )}`
      );
    }
    const response = await oystehr.zambda.execute({
      id: GET_IN_HOUSE_ORDERS_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getCreateInHouseLabOrderResources = async (
  oystehr: Oystehr,
  parameters: GetCreateInHouseLabOrderResourcesParameters
): Promise<GetCreateInHouseLabOrderResourcesResponse> => {
  try {
    if (GET_CREATE_IN_HOUSE_LAB_ORDER_RESOURCES == null) {
      throw new Error('get create in house lab order resources zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: GET_CREATE_IN_HOUSE_LAB_ORDER_RESOURCES,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const collectInHouseLabSpecimen = async (
  oystehr: Oystehr,
  parameters: CollectInHouseLabSpecimenParameters
): Promise<CollectInHouseLabSpecimenZambdaOutput> => {
  try {
    if (COLLECT_IN_HOUSE_LAB_SPECIMEN == null) {
      throw new Error('collect in house lab specimen zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: COLLECT_IN_HOUSE_LAB_SPECIMEN,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const handleInHouseLabResults = async (
  oystehr: Oystehr,
  parameters: HandleInHouseLabResultsParameters
): Promise<HandleInHouseLabResultsZambdaOutput> => {
  try {
    if (HANDLE_IN_HOUSE_LAB_RESULTS == null) {
      throw new Error('handle in house lab results zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: HANDLE_IN_HOUSE_LAB_RESULTS,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const deleteInHouseLabOrder = async (
  oystehr: Oystehr,
  parameters: DeleteInHouseLabOrderParameters
): Promise<DeleteInHouseLabOrderZambdaOutput> => {
  try {
    if (DELETE_IN_HOUSE_LAB_ORDER == null) {
      throw new Error('delete in house lab order zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: DELETE_IN_HOUSE_LAB_ORDER,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getNursingOrders = async (oystehr: Oystehr, parameters: GetNursingOrdersInput): Promise<any> => {
  try {
    const response = await oystehr.zambda.execute({
      id: GET_NURSING_ORDERS_ZAMBDA_ID,
      ...parameters,
    });

    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const createNursingOrder = async (oystehr: Oystehr, parameters: CreateNursingOrderInput): Promise<any> => {
  try {
    const response = await oystehr.zambda.execute({
      id: CREATE_NURSING_ORDER_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const updateNursingOrder = async (oystehr: Oystehr, parameters: UpdateNursingOrderInput): Promise<any> => {
  try {
    const response = await oystehr.zambda.execute({
      id: UPDATE_NURSING_ORDER,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const createDischargeSummary = async (
  oystehr: Oystehr,
  parameters: CreateDischargeSummaryInput
): Promise<CreateDischargeSummaryResponse> => {
  try {
    const response = await oystehr.zambda.execute({
      id: CREATE_DISCHARGE_SUMMARY,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const generatePaperworkPdf = async (
  oystehr: Oystehr,
  parameters: PaperworkToPDFInput
): Promise<{ documentReference: string }> => {
  try {
    const response = await oystehr.zambda.execute({
      id: PAPERWORK_TO_PDF_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getOrCreateVisitDetailsPdf = async (
  oystehr: Oystehr,
  parameters: GetVisitDetailsPDFInput
): Promise<{ documentReference: string }> => {
  try {
    const response = await oystehr.zambda.execute({
      id: VISIT_DETAILS_TO_PDF_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const icd10Search = async (
  oystehr: Oystehr,
  parameters: Icd10SearchRequestParams
): Promise<Icd10SearchResponse> => {
  try {
    const response = await oystehr.zambda.execute({
      id: 'icd-10-search',
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const listTemplates = async (
  oystehr: Oystehr,
  parameters: ListTemplatesZambdaInput
): Promise<ListTemplatesZambdaOutput> => {
  try {
    const response = await oystehr.zambda.execute({
      id: 'list-templates',
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const applyTemplate = async (
  oystehr: Oystehr,
  parameters: ApplyTemplateZambdaInput
): Promise<ApplyTemplateZambdaOutput> => {
  try {
    const response = await oystehr.zambda.execute({
      id: 'apply-template',
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const pendingSupervisorApproval = async (
  oystehr: Oystehr,
  parameters: PendingSupervisorApprovalInput
): Promise<any> => {
  try {
    const response = await oystehr.zambda.execute({
      id: PENDING_SUPERVISOR_APPROVAL_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const unlockAppointment = async (
  oystehr: Oystehr,
  parameters: { appointmentId: string }
): Promise<{ message: string }> => {
  try {
    if (UNLOCK_APPOINTMENT_ZAMBDA_ID == null) {
      throw new Error('unlock appointment zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: UNLOCK_APPOINTMENT_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const sendReceiptByEmail = async (
  oystehr: Oystehr,
  parameters: SendReceiptByEmailZambdaInput
): Promise<SendReceiptByEmailZambdaOutput> => {
  try {
    if (SEND_RECEIPT_BY_EMAIL_ZAMBDA_ID == null) {
      throw new Error('send receipt by email zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: SEND_RECEIPT_BY_EMAIL_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getPatientVisitFiles = async (
  oystehr: Oystehr,
  parameters: { appointmentId: string }
): Promise<VisitDocuments> => {
  try {
    const response = await oystehr.zambda.execute({
      id: 'get-visit-files',
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const getPatientVisitDetails = async (
  oystehr: Oystehr,
  parameters: { appointmentId: string }
): Promise<EHRVisitDetails> => {
  try {
    const response = await oystehr.zambda.execute({
      id: 'ehr-get-visit-details',
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const updatePatientVisitDetails = async (
  oystehr: Oystehr,
  parameters: UpdateVisitDetailsInput
): Promise<void> => {
  try {
    await oystehr.zambda.execute({
      id: 'ehr-update-visit-details',
      ...parameters,
    });
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const updateVisitFiles = async (oystehr: Oystehr, parameters: UpdateVisitFilesInput): Promise<void> => {
  try {
    await oystehr.zambda.execute({
      id: 'update-visit-files',
      ...parameters,
    });
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const invoiceablePatientsReport = async (oystehr: Oystehr): Promise<void> => {
  try {
    const response = await oystehr.zambda.execute({
      id: INVOICEABLE_PATIENTS_REPORT_ZAMBDA_ID,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

export const bulkUpdateInsuranceStatus = async (
  oystehr: Oystehr,
  parameters: BulkUpdateInsuranceStatusInput
): Promise<BulkUpdateInsuranceStatusResponse> => {
  try {
    if (BULK_UPDATE_INSURANCE_STATUS_ZAMBDA_ID == null) {
      throw new Error('bulk update insurance status zambda environment variable could not be loaded');
    }
    const response = await oystehr.zambda.execute({
      id: BULK_UPDATE_INSURANCE_STATUS_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};

const getPresignedFileURL = async (
  params: GetPresignedFileURLInput,
  oystehr: Oystehr
): Promise<PresignUploadUrlResponse> => {
  try {
    const { appointmentID, fileType, fileFormat } = params;

    const response = await oystehr.zambda.executePublic({
      id: 'get-presigned-file-url',
      appointmentID,
      fileType,
      fileFormat,
    });
    const jsonToUse = chooseJson(response);
    return jsonToUse;
  } catch (error: unknown) {
    throw apiErrorToThrow(error);
  }
};

interface CreateZ3ObjectParams {
  appointmentID: string;
  fileType: GetPresignedFileURLInput['fileType'];
  fileFormat: GetPresignedFileURLInput['fileFormat'];
  file: File;
}

export const createZ3Object = async (input: CreateZ3ObjectParams, oystehr: Oystehr): Promise<string> => {
  const { appointmentID, fileType, fileFormat, file } = input;
  try {
    const presignedURLRequest = await getPresignedFileURL(
      {
        appointmentID,
        fileType,
        fileFormat,
      },
      oystehr
    );

    // const presignedURLResponse = await presignedURLRequest.json();
    // Upload the file to S3
    const uploadResponse = await fetch(presignedURLRequest.presignedURL, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    return presignedURLRequest.z3URL;
  } catch (error: unknown) {
    throw apiErrorToThrow(error);
  }
};

export const updateInvoiceTask = async (oystehr: Oystehr, parameters: UpdateInvoiceTaskZambdaInput): Promise<void> => {
  try {
    await oystehr.zambda.execute({
      id: UPDATE_INVOICE_TASK_ZAMBDA_ID,
      ...parameters,
    });
  } catch (error: unknown) {
    console.log(error);
    throw apiErrorToThrow(error);
  }
};

export interface GetPatientQueueInput {
  page: number;
  limit: number;
  practitioner?: string;
}

export interface PatientQueueOutput {
  patients: PatientQueueItem[];
  total: number;
  page: number;
  limit: number;
  debug?: {
    encounterSearchTotal: number;
    encounterEntryLength: number;
    filteredEncountersCount: number;
    searchParams: any[];
  };
}

export interface PatientQueueItem {
  id: string;
  name: string;
  age: number;
  gender: string;
  priority: 'high' | 'medium' | 'low';
  height?: string;
  weight?: string;
  status: string;
  checkInTime: string;
}

export const getPatientQueue = async (
  oystehr: Oystehr,
  parameters: GetPatientQueueInput
): Promise<PatientQueueOutput> => {
  try {
    const response = await oystehr.zambda.execute({
      id: GET_PATIENT_QUEUE_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw apiErrorToThrow(error);
  }
};

// Next Appointments
const NEXT_APPOINTMENTS_ZAMBDA_ID = 'next-appointments';

export interface NextAppointmentsInput {
  practitioner: string;
}

export interface NextAppointmentsOutput {
  appointments: AppointmentItem[];
}

export interface AppointmentItem {
  id: string;
  patientName: string;
  patientId: string;
  date: string;
  time: string;
  type: string;
  description?: string;
}

export const getNextAppointments = async (
  oystehr: Oystehr,
  parameters: NextAppointmentsInput
): Promise<NextAppointmentsOutput> => {
  try {
    const response = await oystehr.zambda.execute({
      id: NEXT_APPOINTMENTS_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw apiErrorToThrow(error);
  }
};
const UNREAD_MESSAGES_ZAMBDA_ID = 'unread-messages';

export const getUnreadMessages = async (
  oystehr: Oystehr,
  parameters: UnreadMessagesInput
): Promise<UnreadMessagesOutput> => {
  try {
    if (UNREAD_MESSAGES_ZAMBDA_ID == null) {
      throw new Error('unread messages environment variable could not be loaded');
    }

    const response = await oystehr.zambda.execute({
      id: UNREAD_MESSAGES_ZAMBDA_ID,
      ...parameters,
    });
    return chooseJson(response);
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }
};
